import './client-info.scss';

import { notification } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import {
  ClientOtpModal,
  SystemNotifications
} from '@/components/Shared/entities';
import { ROUTES } from '@/components/Shared/routes/routes';
import { AllurLoader } from '@/components/Shared/ui';
import { formatPhoneNumber } from '@/services/formatter';
import {
  getRecordWordForm,
  getUserFullName,
  getUserInitials
} from '@/services/helpers/strings';
import { useAppDispatch } from '@/store';
import { setHeaderConfig } from '@/store/app.slice';
import { getClientById } from '@/store/clients.slice';
import type { TDataSource } from '@/types/dataSource';

import { useValidateAutoCreditOtp } from '../../AutoCred';
import { clientsDetailedMapperFromApi } from '../helpers/clientsMapper';
import type { TClientDetailed } from '../types';
import { ClientRequests } from './widgets';

const ClientInfo = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const params = useParams();

  const [client, setClient] = useState<TClientDetailed | null>(null);

  const [loading, setLoading] = useState(false);

  const {
    onClientSubmit,
    onOtpSubmit,
    retryOTP,
    continueVerifying,
    isValidateModalOpened,
    setIsValidateModalOpened
  } = useValidateAutoCreditOtp({});

  const handleCreateNewReq = async () => {
    setIsValidateModalOpened({ openedModal: 'client-info-form' });
  };

  const handleRequestClicked = async (request: TDataSource) => {
    if (request.status.isUnverified) continueVerifying(request);
    else if (request.status.isDraft)
      navigate(ROUTES.autoCreditCreate, {
        state: { requestId: request.id }
      });
    else
      navigate(ROUTES.autoCreditRequest(request.id), {
        state: { requestId: request.id }
      });
  };

  useEffect(() => {
    (async () => {
      if (params.uuid) {
        setLoading(true);
        const response = await dispatch(getClientById(params.uuid));

        setLoading(false);
        if (getClientById.rejected.match(response)) {
          notification.error({
            title: 'Не удалось загрузить клиента. Пожалуйста, попробуйте позже'
          });
          navigate(ROUTES.clients);
          return;
        }

        setClient(clientsDetailedMapperFromApi(response.payload));
      }
    })();
  }, [params.uuid, dispatch]);

  useEffect(() => {
    dispatch(
      setHeaderConfig({
        title: 'Клиент',
        helmetTitle: `Клиент - ${getUserFullName(client, { hide: { middleName: true } })}`
      })
    );
    return () => {
      dispatch(setHeaderConfig({}));
    };
  }, [client]);

  if (loading) {
    return <AllurLoader fullScreen />;
  }

  return (
    <div className="client-info">
      <SystemNotifications />

      <div className="client-info__header">
        <div className="client-info__card">
          <h2 className="client-info__card-title client-info__card-user">
            <span>{getUserFullName(client)}</span>
          </h2>
          <p>ИИН: {client?.clientIin || ''}</p>
        </div>
        <div className="client-info__card">
          <h2 className="client-info__card-title client-info__card-phone">
            <a href={`tel:${client?.phoneNumber?.replace(/[^0-9+]/g, '')}`}>
              {formatPhoneNumber(client?.phoneNumber)}
            </a>
          </h2>
          <p>Номер телефона</p>
        </div>
        <div className="client-info__card">
          <h2 className="client-info__card-title">
            {client?.birthDate
              ? dayjs(client?.birthDate)?.format('DD.MM.YYYY')
              : ''}{' '}
            г.
          </h2>
          <p>
            {client?.birthDate
              ? getRecordWordForm(
                  ['лет', 'год', 'года', 'лет'],
                  dayjs().diff(dayjs(client?.birthDate), 'year')
                )
              : ''}
          </p>
        </div>
        <div className="client-info__card">
          <h2 className="client-info__card-title">
            {client?.gender?.name || ''}
          </h2>
          <p>Пол</p>
        </div>
      </div>

      <div className="client-info__mobile-card">
        <h2 className="client-info__mobile-name">{getUserInitials(client)}</h2>
        <div className="client-info__mobile-title">
          <p className="client-info__mobile-info">
            ИИН: {client?.clientIin || ''},
          </p>
          <p className="client-info__mobile-info">
            {client?.phoneNumber || ''}
          </p>
        </div>
      </div>

      <div className="client-info__content">
        <ClientRequests
          handleCreateNewReq={handleCreateNewReq}
          requests={client?.requests}
          handleRequestInfo={handleRequestClicked}
        />
      </div>

      <ClientOtpModal
        title="Создание заявки"
        subtitle="Заполните данные о клиенте"
        onClientSubmit={onClientSubmit}
        onOtpSubmit={onOtpSubmit}
        onSMSRetry={(phone) => retryOTP({ phone, type: 'SMS' })}
        openedModal={isValidateModalOpened.openedModal}
        setOpenedModal={(modal) =>
          setIsValidateModalOpened({ openedModal: modal })
        }
        onClose={() => setIsValidateModalOpened({ openedModal: '' })}
        defaultPhone={isValidateModalOpened.defaultPhone}
      />
    </div>
  );
};

export default ClientInfo;
